import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const existingSubmissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
      const newSubmission = {
        ...formData,
        id: Date.now(),
        timestamp: new Date().toISOString(),
      };
      existingSubmissions.push(newSubmission);
      localStorage.setItem('contactSubmissions', JSON.stringify(existingSubmissions));

      toast.success('Message sent successfully! We\'ll get back to you soon.');
      
      setFormData({
        name: '',
        businessName: '',
        email: '',
        phone: '',
        projectType: '',
        budget: '',
        message: '',
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#007AFF] mb-4 block" data-testid="contact-label">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="contact-title">
            Let's Build Something{' '}
            <span className="bg-gradient-to-r from-[#007AFF] to-[#00F0FF] bg-clip-text text-transparent">
              Amazing Together
            </span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed" data-testid="contact-description">
            Ready to transform your online presence? Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="glass-card p-8 md:p-10 rounded-2xl mb-8">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#007AFF] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Email</div>
                    <a href="mailto:aayush07300@gmail.com" className="text-zinc-400 hover:text-[#007AFF] transition-colors cursor-hover">
                      aayush07300@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#007AFF] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Phone</div>
                    <a href="tel:+919372214207" className="text-zinc-400 hover:text-[#007AFF] transition-colors cursor-hover">
                      +91 93722 14207
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-location">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#007AFF] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Location</div>
                    <p className="text-zinc-400">
                      Mumbai, Maharashtra, India
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h3 className="text-xl font-bold mb-4">Business Hours</h3>
              <div className="space-y-2 text-zinc-400">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span className="text-white">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span className="text-white">10:00 AM - 4:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="text-white">Closed</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="glass-card p-8 md:p-10 rounded-2xl" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="name">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#007AFF] focus:outline-none transition-colors cursor-hover"
                    placeholder="Your name"
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="businessName">
                    Business Name
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#007AFF] focus:outline-none transition-colors cursor-hover"
                    placeholder="Your business"
                    data-testid="form-business"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="email">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#007AFF] focus:outline-none transition-colors cursor-hover"
                    placeholder="your@email.com"
                    data-testid="form-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="phone">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#007AFF] focus:outline-none transition-colors cursor-hover"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="form-phone"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="projectType">
                    Project Type *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#007AFF] focus:outline-none transition-colors cursor-hover"
                    data-testid="form-project-type"
                  >
                    <option value="">Select project type</option>
                    <option value="clinic">Clinic Website</option>
                    <option value="cafe">Café Website</option>
                    <option value="gaming">Gaming Café</option>
                    <option value="gym">Gym/Fitness Website</option>
                    <option value="startup">Startup Website</option>
                    <option value="ecommerce">E-commerce</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="budget">
                    Budget Range
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#007AFF] focus:outline-none transition-colors cursor-hover"
                    data-testid="form-budget"
                  >
                    <option value="">Select budget</option>
                    <option value="<50k">Less than ₹50,000</option>
                    <option value="50k-100k">₹50,000 - ₹1,00,000</option>
                    <option value="100k-200k">₹1,00,000 - ₹2,00,000</option>
                    <option value=">200k">Above ₹2,00,000</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2" htmlFor="message">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#007AFF] focus:outline-none transition-colors resize-none cursor-hover"
                  placeholder="Tell us about your project..."
                  data-testid="form-message"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-[#007AFF] text-white px-8 py-4 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#0066DD] transition-all glow-border disabled:opacity-50 disabled:cursor-not-allowed cursor-hover"
                data-testid="form-submit"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send className="w-5 h-5" />
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};